import { atom } from 'jotai';
import { isFunction } from 'remeda';

import { FormDataGroup, FormValuesByFormTitle } from '../components/forms/types';
import { combineFormDataV2 } from '../components/forms/utils';
import { chatParticipantsByIdAtom } from '../session/state';
import { AppState, UserType } from '../types';
import { createDerivedWritableAtom, shortUuid } from '../utils';
import { parseIdentity } from '../video-sdk';
import { OutstandingMeasuresResponse } from './measures/useGetClientUserPaperwork';

export enum VisitorState {
  INTRO = AppState.INTRO,
  WAITING = AppState.WAITING,
  /**
   * Basically the same as the loading state, but for manual join. It was easier
   * to add a new state than to rewire the other loading state. We'll eventually
   * remove the loading state.
   */
  JOINING = 'joining',
  LOADING = 'loading',
  MEETING = AppState.MEETING,
  COMPLETED = 'completed',
  TIMEOUT = AppState.TIMEOUT,
  TAB_AWAY = AppState.TAB_AWAY,
}

export enum VisitorDrawerState {
  CHAT = 'CHAT',
  SETTINGS = 'SETTINGS',
  MOOD_CHECK_IN_FORM = 'MOOD_CHECK_IN_FORM',
  PENDING_FORMS = 'PENDING_FORMS',
  PENDING_FORMS_COMPLETED = 'PENDING_FORMS_COMPLETED',
  REFLECTION_TOPICS = 'REFLECTION_TOPICS',
}

export enum VisitorModalState {
  CONSENT = 'CONSENT',
  HELP = 'HELP',
  PROVIDER_NOT_HERE = 'PROVIDER_NOT_HERE',
  ADMISSION = 'ADMISSION',
}

const MEETING_STATES = new Set([VisitorState.LOADING, VisitorState.MEETING, VisitorState.COMPLETED]);

export type OutstandingMeasures = OutstandingMeasuresResponse['outstandingMeasures'];

export type VisitorFormData = FormDataGroup;

export type VisitorAtom = {
  name: string;
  state: VisitorState;
  visitorUuid: string;
  visitorDrawerState: VisitorDrawerState | null;
  activeConversationSid?: string;
  sessionConversationSid?: string;
  /**
   * Short ID for the appointment that the visitor joined the meeting with
   */
  sessionAppointmentShortId?: string;
  customerId: string;
  // This is the provider's monolith PK ID, it's used for certain monolith mutations
  providerId: string;
  providerIsOnline: boolean | null;
  formData: VisitorFormData;
  isHelpModalOpen: boolean;
  visitorAccessDocSid?: string;
  deadline?: Date | null;
  measures: OutstandingMeasures;
  preSessionVibesOpen: boolean;
  isProviderNotHereModalOpen: boolean;
  /**
   * Tracks whether the user has interacted with the Vertical Vibes keyboard
   * If they interact with it, navigate away, then navigate back
   * we can skip the instructions when they return.
   */
  preSessionVibesTutorialCompleted: boolean;
  /**
   * Provided during admission for limited API access
   */
  apiToken?: string;
  hasCompletedClientMeasures: boolean;
  /**
   * Track if the provider initiated restarting the session, to change how we
   * react to room/participant disconnects (e.g. to skip the post-session
   * redirect)
   */
  isRestartingSession: boolean;
  doesClientRequireSafetyGuards: boolean;
  /**
   * Number of times the visitor has been admitted
   */
  admissionAttempts: number;
  /**
   * Number of times the visitor has attempted to join the room
   */
  joinAttempts: number;
  isAdmissionModalOpen: boolean;
  /**
   * ISO date time string when the visitor was admitted to the meeting room.
   * This is generated on the server side when the provider admits the visitor.
   */
  admissionTime?: string;
};

export type CreateVisitorOptions = {
  state?: VisitorState;
  name?: string;
};

export function createVisitor({ state = VisitorState.INTRO, name = '' }: CreateVisitorOptions): VisitorAtom {
  return {
    name,
    state,
    customerId: '',
    visitorUuid: shortUuid(),
    providerId: '',
    visitorDrawerState: null,
    providerIsOnline: null,
    isHelpModalOpen: false,
    formData: { providerForms: [], formResponses: [] },
    measures: [],
    preSessionVibesTutorialCompleted: false,
    preSessionVibesOpen: false,
    hasCompletedClientMeasures: false,
    isRestartingSession: false,
    doesClientRequireSafetyGuards: false,
    isAdmissionModalOpen: false,
    admissionAttempts: 0,
    joinAttempts: 0,
    isProviderNotHereModalOpen: false,
  };
}

export const visitorAtom = atom<VisitorAtom>(createVisitor({ name: '', state: VisitorState.INTRO }));

export const apiTokenAtom = createDerivedWritableAtom(visitorAtom, 'apiToken');

export const visitorNameAtom = createDerivedWritableAtom(visitorAtom, 'name');

export const visitorStateAtom = createDerivedWritableAtom(visitorAtom, 'state');

export const visitorDrawerStateAtom = createDerivedWritableAtom(visitorAtom, 'visitorDrawerState');

export const visitorIsHelpModalOpenAtom = createDerivedWritableAtom(visitorAtom, 'isHelpModalOpen');

/**
 * Short ID for the appointment that the visitor joined the meeting with
 *
 * Should be set when the visitor joins a meeting and unset when the visitor
 * leaves
 */
export const sessionAppointmentShortIdAtom = createDerivedWritableAtom(visitorAtom, 'sessionAppointmentShortId');

export const visitorGroupedFormsAtom = atom((get) => {
  const { providerForms, formResponses } = get(visitorFormData) ?? {};

  if (!providerForms) return;
  return Object.values(combineFormDataV2(formResponses, providerForms)).flat();
});

export const visitorFormValues = atom(
  (get) => get(visitorFormData)?.formValuesByTitle,
  (_get, set, valueOrFunction: FormValuesByFormTitle | ((prev?: FormValuesByFormTitle) => FormValuesByFormTitle)) => {
    set(visitorFormData, (prev) => {
      const prevFormValuesByTitle = prev?.formValuesByTitle;
      return {
        ...prev,
        formValuesByTitle: isFunction(valueOrFunction) ? valueOrFunction(prevFormValuesByTitle) : valueOrFunction,
      };
    });
  },
);

export const visitorFormData = atom(
  (get) => get(visitorAtom).formData,
  (_get, set, valueOrFunction: FormDataGroup | ((prev: FormDataGroup) => FormDataGroup)) => {
    set(visitorAtom, (prev) => ({
      ...prev,
      formData: isFunction(valueOrFunction) ? valueOrFunction(prev?.formData) : valueOrFunction,
    }));
  },
);

export const visitorMeasuresAtom = atom(
  (get) => get(visitorAtom).measures,
  (_get, set, valueOrFunction: OutstandingMeasures | ((prev: OutstandingMeasures) => OutstandingMeasures)) => {
    set(visitorAtom, (prev) => ({
      ...prev,
      measures: isFunction(valueOrFunction) ? valueOrFunction(prev?.measures) : valueOrFunction,
    }));
  },
);

/**
 * When using external APIs, do _not_ use this atom as a reference to the
 * visitor alone, use useVisitorId hook instead
 */
export const visitorUuidAtom = atom((get) => get(visitorAtom).visitorUuid);

export const activeConversationSidAtom = createDerivedWritableAtom(visitorAtom, 'activeConversationSid');

export const shouldShowStaleVersionToastAtom = atom((get) => !MEETING_STATES.has(get(visitorAtom).state));

export const providerParticipantAtom = atom((get) =>
  Object.values(get(chatParticipantsByIdAtom)).find(
    (participant) => parseIdentity(participant.identity).userType === UserType.PROVIDER,
  ),
);

export const providerIsOnlineAtom = createDerivedWritableAtom(visitorAtom, 'providerIsOnline');

export const visitorAccessDocSidAtom = createDerivedWritableAtom(visitorAtom, 'visitorAccessDocSid');

export const preSessionVibesOpenAtom = createDerivedWritableAtom(visitorAtom, 'preSessionVibesOpen');

export const preSessionVibesTutorialCompletedAtom = createDerivedWritableAtom(
  visitorAtom,
  'preSessionVibesTutorialCompleted',
);

export const hasCompletedClientMeasuresAtom = createDerivedWritableAtom(visitorAtom, 'hasCompletedClientMeasures');

export const isRestartingSessionAtom = createDerivedWritableAtom(visitorAtom, 'isRestartingSession');

export const doesClientRequireSafetyGuardsAtom = createDerivedWritableAtom(
  visitorAtom,
  'doesClientRequireSafetyGuards',
);
